import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './TermesConditions.css';

export default function TermesConditions() {
  const [termes,  setTermes]  = useState([]);
  const [actif,   setActif]   = useState(null);
  const [contenu, setContenu] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState('');
  const [erreur,  setErreur]  = useState('');
  const [modifie, setModifie] = useState(false);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur('');
      const data = await api.get('/corpo/termes');
      setTermes(Array.isArray(data) ? data : []);
      const v = (Array.isArray(data) ? data : []).find(t => t.actif) || (data[0] || null);
      if (v) {
        setActif(v);
        setContenu(v.contenu);
      }
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  async function sauvegarder() {
    if (!actif) return;
    try {
      setSaving(true);
      await api.put(`/corpo/termes/${actif.id}`, { contenu });
      showToast('Termes sauvegardés');
      setModifie(false);
      await charger();
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function creerVersion() {
    if (!window.confirm('Créer une nouvelle version des termes ? L\'ancienne version sera conservée en historique.')) return;
    try {
      setSaving(true);
      const nouveau = await api.post('/corpo/termes', { contenu });
      showToast(`Version ${nouveau.version} créée`);
      setModifie(false);
      await charger();
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  function selectionnerVersion(t) {
    if (modifie && !window.confirm('Abandonner les modifications non sauvegardées ?')) return;
    setActif(t);
    setContenu(t.contenu);
    setModifie(false);
  }

  function showToast(m) {
    setToast(m);
    setTimeout(() => setToast(''), 3000);
  }

  function fmtDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('fr-CA', { dateStyle: 'medium' });
  }

  // ── Skeleton ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="tc-page">
        <div className="tc-page-header">
          <div className="tc-page-header-text">
            <div className="skeleton" style={{ height: 36, width: 280, marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: 200 }} />
          </div>
        </div>
        <div className="tc-layout">
          <div className="card" style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 320 }} />
          </div>
          <div className="tc-sidebar">
            <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
              <div className="skeleton" style={{ height: 160 }} />
            </div>
            <div className="card">
              <div className="skeleton" style={{ height: 200 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tc-page">
      {/* Toast */}
      {toast && (
        <div className="tc-toast rise-in">
          <Icon name="succes" size={15} />
          {toast}
        </div>
      )}

      {/* En-tête */}
      <div className="tc-page-header">
        <div className="tc-page-header-text">
          <h1 className="page-title">Termes &amp; Conditions</h1>
          <p className="tc-subtitle muted">Gestion des versions des termes et conditions clients</p>
        </div>
        <div className="tc-header-actions">
          <button
            className="btn btn-ghost"
            onClick={sauvegarder}
            disabled={saving || !modifie}
          >
            <Icon name="sauvegarder" size={15} />
            {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
          <button
            className="btn btn-primary"
            onClick={creerVersion}
            disabled={saving || !modifie}
          >
            <Icon name="ajouter" size={15} />
            Nouvelle version
          </button>
        </div>
      </div>

      {/* Erreur */}
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-5)' }}>
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      <div className="tc-layout">
        {/* ── Éditeur ───────────────────────────────────────────────────────── */}
        <div className="card tc-editor rise-in">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon">
                <Icon name="document" size={15} />
              </span>
              <span className="card-title">
                {actif ? `Version ${actif.version}` : 'Nouvelle version'}
              </span>
              {actif?.actif && <span className="badge badge--success">Active</span>}
              {actif && !actif.actif && <span className="badge">Archivée</span>}
              {modifie && <span className="badge badge--warning">Modifié</span>}
            </div>
          </div>

          <p className="tc-editor-hint muted">
            Markdown supporté : # Titre, ## Sous-titre, **gras**, *italique*, - liste
          </p>

          <textarea
            className="tc-textarea"
            value={contenu}
            onChange={e => { setContenu(e.target.value); setModifie(true); }}
            rows={28}
            placeholder="Saisissez les termes et conditions en Markdown…"
          />
        </div>

        {/* ── Colonne droite ────────────────────────────────────────────────── */}
        <div className="tc-sidebar">
          {/* Aperçu rendu */}
          <div className="card tc-apercu-card rise-in" style={{ animationDelay: '60ms' }}>
            <div className="card-header">
              <div className="card-header-left">
                <span className="card-header-icon">
                  <Icon name="voir" size={15} />
                </span>
                <span className="card-title">Aperçu</span>
              </div>
            </div>
            <div className="tc-apercu">
              {contenu
                ? contenu.split('\n').map((ligne, i) => {
                    if (ligne.startsWith('### ')) return <h5 key={i} className="tc-md-h3">{ligne.slice(4)}</h5>;
                    if (ligne.startsWith('## '))  return <h4 key={i} className="tc-md-h2">{ligne.slice(3)}</h4>;
                    if (ligne.startsWith('# '))   return <h3 key={i} className="tc-md-h1">{ligne.slice(2)}</h3>;
                    if (ligne.startsWith('- '))   return <li key={i} className="tc-md-li">{ligne.slice(2)}</li>;
                    if (ligne === '')              return <br key={i} />;
                    return <p key={i} className="tc-md-p">{ligne}</p>;
                  })
                : (
                  <div className="empty-state" style={{ paddingTop: 'var(--space-5)', paddingBottom: 'var(--space-5)' }}>
                    <Icon name="document" size={32} />
                    <span>Saisissez du contenu pour voir l'aperçu</span>
                  </div>
                )}
            </div>
          </div>

          {/* Historique des versions */}
          <div className="card rise-in" style={{ animationDelay: '120ms' }}>
            <div className="card-header">
              <div className="card-header-left">
                <span className="card-header-icon">
                  <Icon name="heure" size={15} />
                </span>
                <span className="card-title">Historique des versions</span>
              </div>
            </div>
            {termes.length === 0 ? (
              <div className="empty-state" style={{ paddingTop: 'var(--space-5)', paddingBottom: 'var(--space-5)' }}>
                <Icon name="liste" size={28} />
                <span>Aucune version</span>
              </div>
            ) : (
              <div className="tc-versions-liste">
                {termes.map(t => (
                  <div
                    key={t.id}
                    className={`tc-version-item ${actif?.id === t.id ? 'tc-version-item--active' : ''}`}
                    onClick={() => selectionnerVersion(t)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && selectionnerVersion(t)}
                  >
                    <div className="tc-version-header">
                      <span className="tc-version-num">Version {t.version}</span>
                      {t.actif
                        ? <span className="badge badge--success" style={{ fontSize: 10 }}>Active</span>
                        : <span className="badge" style={{ fontSize: 10 }}>Archivée</span>}
                    </div>
                    <div className="tc-version-date muted">{fmtDate(t.cree_le)}</div>
                    <div className="tc-version-extrait muted">
                      {t.contenu.slice(0, 80).replace(/[#*]/g, '')}…
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
